import { uniq } from "lodash";
import set from "lodash/set";
import { setIncludes } from "@/utilities/promotions/setIncludes";
import type { Context } from "@/utilities/promotions/models/context";
import type { TokenAllocation } from "hooks/firestore/models/tokenAllocation";
import type { TokenAutomation } from "hooks/firestore/models/tokenAutomation";
import type { TimesAllowedToCompleteFilterKey } from "hooks/firestore/models/baseAutomation";
import type {
  BetSelection,
  BetslipHub,
  SemSelection,
} from "@/features/betslip/types";
import type { BetslipStore } from "@/features/betslip/stores/types";

type AllocationWithId = TokenAllocation & { id: string };

export type TokenContextState = Pick<
  BetslipStore,
  "selections" | "stakes" | "promoCash" | "tokens"
>;

const MULTI_OUTCOME_ID = "MULTI";

const semLegs = (selection: BetSelection): SemSelection["legs"] =>
  selection.kind === "sem" ? selection.legs : [];

const deriveEventType = (
  selection: BetSelection,
): "MATCH" | "SEASON" | "TOURNAMENT" | "RACE" | undefined => {
  if (
    selection.kind === "race" ||
    selection.kind === "exotic" ||
    selection.kind === "quaddie"
  ) {
    return "RACE";
  }
  if (selection.hub === "racing") return "RACE";
  if (selection.kind === "outright") {
    if ("seasonId" in selection && selection.seasonId) return "SEASON";
    return "TOURNAMENT";
  }
  return "MATCH";
};

const collectIds = (
  selections: BetSelection[],
  field: "eventId" | "marketId" | "sport" | "marketType",
): string[] =>
  uniq(
    selections.flatMap((s) => {
      const own = s[field as keyof BetSelection];
      const legs = semLegs(s);
      const fromLegs = legs
        .map((leg) => {
          if (field === "marketId") return leg.marketId;
          if (field === "marketType") return leg.marketType;
          return undefined;
        })
        .filter(Boolean) as string[];
      return [own as string, ...fromLegs].filter(Boolean) as string[];
    }),
  );

const collectOptionalIds = (
  selections: BetSelection[],
  field: "seasonId" | "tournamentId" | "meetingId",
): string[] =>
  uniq(
    selections
      .map((s) => (field in s ? (s as any)[field] : undefined))
      .filter(Boolean) as string[],
  );

const collectOutcomeIds = (selection: BetSelection): string[] => {
  const legs = semLegs(selection);
  if (legs.length)
    return [selection.outcomeId, ...legs.map((l) => l.outcomeId)];
  return [selection.outcomeId];
};

// One value per real outcome on the bet (SEM legs individually), not
// deduped, so outcomes without one still count. Exotics and quaddies hold
// runners per position rather than per outcome, so they contribute none and
// the per-outcome stake filters fail closed.
const collectOutcomeValues = (
  selections: BetSelection[],
  field: "competitorId" | "outcomeGroup",
): (string | undefined)[] =>
  selections.flatMap((s) => {
    if (s.kind === "sem") return s.legs.map((leg) => leg[field]);
    if (s.kind === "match" || s.kind === "outright" || s.kind === "race") {
      return [s[field]];
    }
    return [];
  });

const stakeSourceFor = (isUsingPromo: boolean) =>
  isUsingPromo ? ("PROMOTION_USER" as const) : ("AVAILABLE" as const);

const isHubRacing = (hub: BetslipHub): boolean => hub === "racing";

/**
 * Build the per-selection token Context. For singles, pass `[selection]`;
 * for the multi pass every multi leg.
 */
export const buildSelectionContext = (
  state: TokenContextState,
  selections: BetSelection[],
  isMulti: boolean,
): Context["selection"] => {
  const eventIds = collectIds(selections, "eventId");
  const marketIds = collectIds(selections, "marketId");
  const sports = collectIds(selections, "sport");
  const marketTypes = collectIds(selections, "marketType");
  const seasonIds = collectOptionalIds(selections, "seasonId");
  const tournamentIds = collectOptionalIds(selections, "tournamentId");
  const meetingIds = collectOptionalIds(selections, "meetingId");

  const outcomeIds = uniq(selections.flatMap(collectOutcomeIds));
  const competitorIds = collectOutcomeValues(selections, "competitorId");
  const outcomeGroups = collectOutcomeValues(selections, "outcomeGroup");

  const outcomes: Context["selection"]["outcomes"] = selections.map((s) => {
    const stake = state.stakes.singles[s.outcomeId] ?? 0;
    const isPromo = state.promoCash.singles[s.outcomeId] ?? false;
    const allocationId = state.tokens.singles[s.outcomeId] ?? null;
    return {
      id: s.outcomeId,
      eventId: s.eventId,
      odds: s.odds,
      stake,
      allocationId,
      eventType: deriveEventType(s),
      hasPromotionalMarkets: !!s.hasPromotionalMarkets,
      hasLiveMarkets: !!s.hasLiveMarkets,
      stakeSource: stakeSourceFor(isPromo),
      subOutcomesCount: semLegs(s).length,
      expectedResults: "expectedResults" in s ? s.expectedResults ?? {} : {},
    };
  });

  if (isMulti) {
    const multiOdds =
      selections.length > 0
        ? selections.reduce((acc, s) => acc * s.odds, 1)
        : 0;

    return {
      isMulti,
      odds: multiOdds,
      stake: state.stakes.multi,
      stakeSource: stakeSourceFor(state.promoCash.multi),
      subOutcomesCount: 0,
      subOutcomes: [],
      expectedResults: {},
      outcomes,
      competitorIds,
      outcomeGroups,
      eventIds,
      outcomeIds,
      marketIds,
      meetingIds,
      sports,
      marketTypes,
      tournamentIds,
      seasonIds,
    };
  }

  // Single (or SEM rendered as a single card). Use the parent selection's
  // own fields for the top-level context.
  const parent = selections[0]!;
  const stake = state.stakes.singles[parent.outcomeId] ?? 0;
  const isPromo = state.promoCash.singles[parent.outcomeId] ?? false;
  const allocationId = state.tokens.singles[parent.outcomeId] ?? null;

  const subOutcomes: Context["selection"]["subOutcomes"] = semLegs(parent).map(
    (leg) => ({
      name: leg.name,
      outcomeId: leg.outcomeId,
      marketId: leg.marketId,
      marketName: leg.marketName,
      marketType: leg.marketType,
      outcomeType: leg.outcomeType,
      odds: leg.odds,
      type: leg.outcomeType,
    }),
  );

  return {
    isMulti,
    odds: parent.odds,
    stake,
    allocationId,
    eventType: deriveEventType(parent),
    hasPromotionalMarkets: !!parent.hasPromotionalMarkets,
    hasLiveMarkets: !!parent.hasLiveMarkets,
    stakeSource: stakeSourceFor(isPromo),
    subOutcomesCount: semLegs(parent).length,
    subOutcomes,
    expectedResults:
      "expectedResults" in parent ? parent.expectedResults ?? {} : {},
    minSelectionOdds: semLegs(parent)
      .map((leg) => leg.odds)
      .filter((n): n is number => typeof n === "number")[0],
    outcomes,
    competitorIds,
    outcomeGroups,
    eventIds,
    outcomeIds,
    marketIds,
    meetingIds: isHubRacing(parent.hub)
      ? collectOptionalIds([parent], "meetingId")
      : meetingIds,
    sports,
    marketTypes,
    tournamentIds,
    seasonIds,
  };
};

/**
 * Walk every selection in the slip and build the per-automation per-event
 * allocation usage map.
 */
export const buildUsagesContext = (
  state: TokenContextState,
  allocations: AllocationWithId[] = [],
  automations: TokenAutomation[] = [],
  selectionContext: Context["selection"],
): Context["usages"] => {
  const filterConfigHandlers = new Map<
    TimesAllowedToCompleteFilterKey,
    Array<string>
  >([
    ["eventIds", selectionContext.eventIds],
    ["marketIds", selectionContext.marketIds],
    ["marketTypeNames", selectionContext.marketTypes],
    ["sports", selectionContext.sports],
    ["meetingIds", selectionContext.meetingIds],
    ["tournamentIds", selectionContext.tournamentIds],
    ["seasonIds", selectionContext.seasonIds ?? []],
  ]);

  const isUsageTracked = (automation: Context["automation"]): boolean => {
    const requirement = (automation?.requirements ?? []).find(
      (r) => !!r.userDrivenAttributes,
    );
    const filterList =
      requirement?.userDrivenAttributes?.timesAllowedToCompletePerEventFilter ??
      [];
    return filterList.some((filter) => {
      const valuesFromSelection = new Set(filterConfigHandlers.get(filter));
      const valuesFromConfiguration = new Set(
        requirement?.userDrivenAttributes?.[filter],
      );
      return valuesFromSelection
        ? setIncludes(valuesFromConfiguration, valuesFromSelection)
        : true;
    });
  };

  const result: Context["usages"] = {};

  for (const [outcomeId, allocationId] of Object.entries(
    state.tokens.singles,
  )) {
    if (!allocationId) continue;
    const selection = state.selections[outcomeId];
    if (!selection) continue;
    const allocation = allocations.find((a) => a.id === allocationId);
    if (!allocation) continue;
    const automation = automations.find(
      (a) => a.automationId === allocation.automationId,
    );
    if (!automation) continue;
    const requirement = (automation.requirements ?? []).find(
      (r) => !!r.userDrivenAttributes,
    );
    if (
      !requirement?.userDrivenAttributes?.timesAllowedToCompletePerEventFilter
    )
      continue;

    if (!isUsageTracked(automation)) continue;

    const existing = result[automation.automationId]?.[selection.eventId] ?? [];
    set(result, `${automation.automationId}.${selection.eventId}`, [
      ...existing,
      allocation.id,
    ]);
  }

  return result;
};

/**
 * Returns a map from allocationId to the outcome+event it's currently
 * attached to. Multi allocations use `MULTI_OUTCOME_ID` as the outcomeId
 * so consumers can detect the multi case.
 */
export const buildUsedAllocations = (
  state: TokenContextState,
): Context["usedAllocations"] => {
  const result: Context["usedAllocations"] = {};

  for (const [outcomeId, allocationId] of Object.entries(
    state.tokens.singles,
  )) {
    if (!allocationId) continue;
    const selection = state.selections[outcomeId];
    if (!selection) continue;
    result[allocationId] = {
      outcomeId,
      eventId: selection.eventId,
    };
  }

  if (state.tokens.multi) {
    const firstMultiLeg = Object.values(state.selections).find(
      (s) => s.isMultiable,
    );
    if (firstMultiLeg) {
      result[state.tokens.multi] = {
        outcomeId: MULTI_OUTCOME_ID,
        eventId: firstMultiLeg.eventId,
      };
    }
  }

  return result;
};

export { MULTI_OUTCOME_ID };
